import React from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Cell,
} from "recharts";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  ChartLegend,
  ChartLegendContent,
} from "ui/components/chart";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "ui/components/card";
// removed year selection UI as API doesn't support filters
import { useActualVsExtendedBilling } from "../hooks/useActualVsExtendedBilling";
import { DisplayAmount } from "components/display/amount";
import { formatAmount } from "utils/utils";
import { generateYAxisTicks } from "@/lib/utils";
import ChartLoader from "@/components/loader/chart";

const chartConfig = {
  original_billing: {
    label: "Original Billing",
    color: "#3b82f6", // blue-500
  },
  final_billing: {
    label: "Final Billing",
    color: "#f59e0b", // amber-500
  },
};

const getCurrentYear = () => {
  const now = new Date();
  const month = now.getMonth() + 1;
  const year = now.getFullYear();
  return month <= 3 ? year - 1 : year;
};

export function ActualVsExtendedChart() {
  const selectedYear = getCurrentYear().toString();

  const { data, isLoading, error } = useActualVsExtendedBilling({
    year: parseInt(selectedYear),
  });

  if (error) {
    return (
      <Card className="mx-6 mt-4">
        <CardContent className="pt-6">
          <div className="text-center py-8 text-destructive">
            Error loading chart data: {error.message}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card className="mx-6 mt-4 border-none">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Original vs Final Billing</CardTitle>
              <CardDescription>
                Monthly comparison of original and final billing for{" "}
                {selectedYear}
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <ChartLoader />
          ) : !data || data.length === 0 ? (
            <div className="flex h-[400px] items-center justify-center text-muted-foreground">
              No data available for {selectedYear}
            </div>
          ) : (
            (() => {
              const allValues = data.flatMap((item) => [
                item.original_billing,
                item.final_billing,
              ]);
              const maxValue = Math.max(...allValues);
              const yAxisMax = Math.ceil(maxValue * 1.1);
              const yAxisTicks = generateYAxisTicks(yAxisMax);

              return (
                <ChartContainer
                  config={chartConfig}
                  className="h-[400px] w-full"
                >
                  <BarChart
                    accessibilityLayer
                    data={data}
                    margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                    barCategoryGap="20%"
                    barGap={4}
                  >
                    <CartesianGrid vertical={false} />
                    <XAxis
                      dataKey="year_month"
                      tickLine={false}
                      tickMargin={10}
                      axisLine={false}
                      fontSize={12}
                    />
                    <YAxis
                      tickLine={false}
                      axisLine={false}
                      fontSize={12}
                      tickFormatter={formatAmount}
                      domain={[0, yAxisMax]}
                      ticks={yAxisTicks}
                    />
                    <ChartTooltip
                      content={({ active, payload }) => {
                        if (!active || !payload || payload.length === 0)
                          return null;

                        const row = payload[0].payload;
                        return (
                          <div className="bg-background border border-border rounded-lg shadow-lg p-3">
                            <div className="font-semibold text-foreground mb-2">
                              {row.year_month}
                            </div>
                            <div className="space-y-2">
                              <div className="flex items-center justify-between gap-4">
                                <div className="flex items-center gap-2">
                                  <div
                                    className="w-2 h-2 rounded-sm"
                                    style={{
                                      backgroundColor:
                                        "var(--color-original_billing)",
                                    }}
                                  />
                                  <span>
                                    {chartConfig.original_billing.label}
                                  </span>
                                </div>
                                <DisplayAmount
                                  value={row.original_billing}
                                  format={true}
                                />
                              </div>
                              <div className="flex items-center justify-between gap-4">
                                <div className="flex items-center gap-2">
                                  <div
                                    className="w-2 h-2 rounded-sm"
                                    style={{
                                      backgroundColor:
                                        "var(--color-final_billing)",
                                    }}
                                  />
                                  <span>
                                    {chartConfig.final_billing.label}
                                  </span>
                                </div>
                                <DisplayAmount
                                  value={row.final_billing}
                                  format={true}
                                />
                              </div>
                            </div>
                          </div>
                        );
                      }}
                    />
                    <ChartLegend content={<ChartLegendContent />} />
                    <Bar
                      dataKey="original_billing"
                      fill="var(--color-original_billing)"
                      radius={[4, 4, 0, 0]}
                      name="Original Billing"
                    />
                    <Bar
                      dataKey="final_billing"
                      fill="var(--color-final_billing)"
                      radius={[4, 4, 0, 0]}
                      name="Final Billing"
                    />
                  </BarChart>
                </ChartContainer>
              );
            })()
          )}
        </CardContent>
      </Card>


      <Card className="mx-6 mt-4 border-none">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Monthly Slippage</CardTitle>
              <CardDescription>
                Monthly difference between extended and actual billing for{" "}
                {selectedYear}
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <ChartLoader />
          ) : !data || data.length === 0 ? (
            <div className="flex h-[400px] items-center justify-center text-muted-foreground">
              No data available for {selectedYear}
            </div>
          ) : (
            (() => {
              const diffValues = data.map((d) =>
                Math.abs(d.billing_difference || 0)
              );
              const maxValue = diffValues.length ? Math.max(...diffValues) : 0;
              const yAxisMax = Math.ceil(maxValue * 1.1);
              const yAxisTicks = generateYAxisTicks(yAxisMax);

              return (
                <ChartContainer
                  config={{
                    billing_difference: {
                      label: "Billing Difference",
                      color: "#10b981",
                    },
                  }}
                  className="h-[400px] w-full"
                >
                  <BarChart
                    accessibilityLayer
                    data={data}
                    margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                    barCategoryGap="25%"
                  >
                    <CartesianGrid vertical={false} />
                    <XAxis
                      dataKey="year_month"
                      tickLine={false}
                      tickMargin={10}
                      axisLine={false}
                      fontSize={12}
                    />
                    <YAxis
                      tickLine={false}
                      axisLine={false}
                      fontSize={12}
                      tickFormatter={formatAmount}
                      domain={[-yAxisMax, yAxisMax]}
                      ticks={[
                        ...yAxisTicks
                          .map((t) => -t)
                          .reverse()
                          .slice(1),
                        ...yAxisTicks,
                      ]}
                    />
                    <ChartTooltip
                      content={({ active, payload }) => {
                        if (!active || !payload || payload.length === 0)
                          return null;
                        const row = payload[0].payload;
                        return (
                          <div className="bg-background border border-border rounded-lg shadow-lg p-3">
                            <div className="font-semibold text-foreground mb-2">
                              {row.year_month}
                            </div>
                            <div className="flex items-center justify-between gap-4">
                              <div className="flex items-center gap-2">
                                <div
                                  className="w-2 h-2 rounded-sm"
                                  style={{
                                    backgroundColor:
                                      row.billing_difference < 0
                                        ? "#ef4444"
                                        : "#10b981",
                                  }}
                                />
                                <span>Billing Difference</span>
                              </div>
                              <DisplayAmount
                                value={Math.abs(row.billing_difference)}
                                format={true}
                              />
                            </div>
                          </div>
                        );
                      }}
                    />
                    <ChartLegend content={<ChartLegendContent />} />
                    <Bar
                      dataKey="billing_difference"
                      fill="var(--color-billing_difference)"
                      radius={[4, 4, 0, 0]}
                      name="Billing Difference"
                    >
                      {data.map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={
                            entry.billing_difference < 0 ? "#ef4444" : "#10b981"
                          }
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ChartContainer>
              );
            })()
          )}
        </CardContent>
      </Card>
    </>
  );
}
